import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "./Logo";
import { NAV_LINKS } from "../data/content";
import { scrollToId, PHONE, PHONE_HREF, JOBBER_CLIENT_HUB_URL } from "../lib/scroll";

export const openClientHub = (e) => {
  if (JOBBER_CLIENT_HUB_URL) return;
  e.preventDefault();
  toast("Client Hub launching soon", { description: "Secure Jobber client login will be connected here." });
};

const go = (id, after) => (e) => {
  e.preventDefault();
  scrollToId(id);
  if (after) after();
};

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
        scrolled || open ? "bg-[#0B0C10]/90 backdrop-blur-xl border-white/10" : "bg-[#0B0C10]/30 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 h-[72px] flex items-center justify-between gap-6">
        <Logo onClick={go("top")} />
        <nav className="hidden xl:flex items-center gap-7" aria-label="Main">
          {NAV_LINKS.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              onClick={go(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="nav-link text-[13px] font-medium text-zinc-300 hover:text-white"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <a href={PHONE_HREF} data-testid="nav-phone" className="hidden lg:flex items-center gap-2 font-mono text-xs text-zinc-300 hover:text-[#FF6B00] transition-colors">
            <Phone className="h-3.5 w-3.5 text-[#FF6B00]" /> {PHONE}
          </a>
          <a href={JOBBER_CLIENT_HUB_URL || "#client-login"} onClick={openClientHub} data-testid="nav-client-login" className="btn-outline">
            Client Login
          </a>
          <a href="#contact" onClick={go("contact")} data-testid="nav-request-service" className="btn-primary">
            Request Service
          </a>
        </div>
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          className="md:hidden h-11 w-11 grid place-items-center rounded-full border border-white/15 text-white"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="tablet-menu-toggle"
          aria-label="Toggle menu"
          className="hidden md:grid xl:hidden h-11 w-11 place-items-center rounded-full border border-white/15 text-white -ml-3"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-drawer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden overflow-hidden border-t border-white/10 bg-[#0B0C10]"
          >
            <div className="px-5 sm:px-8 py-6 flex flex-col">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.id}
                  href={`#${l.id}`}
                  onClick={go(l.id, () => setOpen(false))}
                  data-testid={`drawer-link-${l.id}`}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i + 0.1 }}
                  className="flex items-center justify-between py-3.5 border-b border-white/10 font-display text-2xl font-semibold text-white"
                >
                  {l.label}
                  <ArrowUpRight className="h-5 w-5 text-[#FF6B00]" />
                </motion.a>
              ))}
              <div className="grid grid-cols-2 gap-3 mt-6">
                <a href={PHONE_HREF} data-testid="drawer-call-now" className="btn-outline justify-center">
                  <Phone className="h-4 w-4" /> Call Now
                </a>
                <a href="#contact" onClick={go("contact", () => setOpen(false))} data-testid="drawer-request-service" className="btn-primary justify-center">
                  Request Service
                </a>
              </div>
              <a href={JOBBER_CLIENT_HUB_URL || "#client-login"} onClick={openClientHub} data-testid="drawer-client-login" className="mt-4 text-center text-sm text-zinc-400 underline underline-offset-4">
                Client Login
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
